import React, { useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Button, Col, Row } from 'reactstrap';
import { Translate, ValidatedField, ValidatedForm, translate } from 'react-jhipster';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { useAppDispatch, useAppSelector } from 'app/config/store';

import { getEntities as getProductOrders } from 'app/entities/product-order/product-order.reducer';
import { getEntities as getProductOfferings } from 'app/entities/product-offering/product-offering.reducer';
import { getEntities as getEans } from 'app/entities/ean/ean.reducer';
import { ProductStatus } from 'app/shared/model/enumerations/product-status.model';
import { FeedType } from 'app/shared/model/enumerations/feed-type.model';
import { FeedInType } from 'app/shared/model/enumerations/feed-in-type.model';
import { ProductType } from 'app/shared/model/enumerations/product-type.model';
import { createEntity, getEntity, reset, updateEntity } from './product.reducer';

export const ProductUpdate = () => {
  const dispatch = useAppDispatch();

  const navigate = useNavigate();

  const { id } = useParams<'id'>();
  const isNew = id === undefined;

  const productOrders = useAppSelector(state => state.productOrder.entities);
  const productOfferings = useAppSelector(state => state.productOffering.entities);
  const eans = useAppSelector(state => state.ean.entities);
  const productEntity = useAppSelector(state => state.product.entity);
  const loading = useAppSelector(state => state.product.loading);
  const updating = useAppSelector(state => state.product.updating);
  const updateSuccess = useAppSelector(state => state.product.updateSuccess);
  const productStatusValues = Object.keys(ProductStatus);
  const feedTypeValues = Object.keys(FeedType);
  const feedInTypeValues = Object.keys(FeedInType);
  const productTypeValues = Object.keys(ProductType);

  const handleClose = () => {
    navigate('/product');
  };

  useEffect(() => {
    if (isNew) {
      dispatch(reset());
    } else {
      dispatch(getEntity(id));
    }

    dispatch(getProductOrders({}));
    dispatch(getProductOfferings({}));
    dispatch(getEans({}));
  }, []);

  useEffect(() => {
    if (updateSuccess) {
      handleClose();
    }
  }, [updateSuccess]);

  const saveEntity = values => {
    if (values.id !== undefined && typeof values.id !== 'number') {
      values.id = Number(values.id);
    }
    if (
      values.initialEstimatedYearlyQuantity !== undefined &&
      typeof values.initialEstimatedYearlyQuantity !== 'number'
    ) {
      values.initialEstimatedYearlyQuantity = Number(values.initialEstimatedYearlyQuantity);
    }
    if (
      values.retrievedYearlyQuantityReading !== undefined &&
      typeof values.retrievedYearlyQuantityReading !== 'number'
    ) {
      values.retrievedYearlyQuantityReading = Number(values.retrievedYearlyQuantityReading);
    }
    if (values.initialFixedAnnualPrice !== undefined && typeof values.initialFixedAnnualPrice !== 'number') {
      values.initialFixedAnnualPrice = Number(values.initialFixedAnnualPrice);
    }
    if (values.usage !== undefined && typeof values.usage !== 'number') {
      values.usage = Number(values.usage);
    }

    const entity = {
      ...productEntity,
      ...values,
      productOrder: productOrders.find(it => it.id.toString() === values.productOrder?.toString()),
      productOffering: productOfferings.find(it => it.id.toString() === values.productOffering?.toString()),
      ean: eans.find(it => it.id.toString() === values.ean?.toString()),
    };

    if (isNew) {
      dispatch(createEntity(entity));
    } else {
      dispatch(updateEntity(entity));
    }
  };

  const defaultValues = () =>
    isNew
      ? {}
      : {
          productStatus: 'NEW',
          feedType: 'IN',
          feedInType: 'SOLAR',
          productType: 'ELECTRICITY',
          ...productEntity,
          productOrder: productEntity?.productOrder?.id,
          productOffering: productEntity?.productOffering?.id,
          ean: productEntity?.ean?.id,
        };

  return (
    <div>
      <Row className="justify-content-center">
        <Col md="8">
          <h2 id="crawlerBackendApp.product.home.createOrEditLabel" data-cy="ProductCreateUpdateHeading">
            <Translate contentKey="crawlerBackendApp.product.home.createOrEditLabel">
              Create or edit a Product
            </Translate>
          </h2>
        </Col>
      </Row>
      <Row className="justify-content-center">
        <Col md="8">
          {loading ? (
            <p>Loading...</p>
          ) : (
            <ValidatedForm defaultValues={defaultValues()} onSubmit={saveEntity}>
              {!isNew ? (
                <ValidatedField
                  name="id"
                  required
                  readOnly
                  id="product-id"
                  label={translate('global.field.id')}
                  validate={{ required: true }}
                />
              ) : null}
              <ValidatedField
                label={translate('crawlerBackendApp.product.productStatus')}
                id="product-productStatus"
                name="productStatus"
                data-cy="productStatus"
                type="select"
              >
                {productStatusValues.map(productStatus => (
                  <option value={productStatus} key={productStatus}>
                    {translate(`crawlerBackendApp.ProductStatus.${productStatus}`)}
                  </option>
                ))}
              </ValidatedField>
              <ValidatedField
                label={translate('crawlerBackendApp.product.feedType')}
                id="product-feedType"
                name="feedType"
                data-cy="feedType"
                type="select"
              >
                {feedTypeValues.map(feedType => (
                  <option value={feedType} key={feedType}>
                    {translate(`crawlerBackendApp.FeedType.${feedType}`)}
                  </option>
                ))}
              </ValidatedField>
              <ValidatedField
                label={translate('crawlerBackendApp.product.feedInType')}
                id="product-feedInType"
                name="feedInType"
                data-cy="feedInType"
                type="select"
              >
                {feedInTypeValues.map(feedInType => (
                  <option value={feedInType} key={feedInType}>
                    {translate(`crawlerBackendApp.FeedInType.${feedInType}`)}
                  </option>
                ))}
              </ValidatedField>
              <ValidatedField
                label={translate('crawlerBackendApp.product.productType')}
                id="product-productType"
                name="productType"
                data-cy="productType"
                type="select"
              >
                {productTypeValues.map(productType => (
                  <option value={productType} key={productType}>
                    {translate(`crawlerBackendApp.ProductType.${productType}`)}
                  </option>
                ))}
              </ValidatedField>
              <ValidatedField
                label={translate('crawlerBackendApp.product.initialEstimatedYearlyQuantity')}
                id="product-initialEstimatedYearlyQuantity"
                name="initialEstimatedYearlyQuantity"
                data-cy="initialEstimatedYearlyQuantity"
                type="text"
              />
              <ValidatedField
                label={translate('crawlerBackendApp.product.retrievedYearlyQuantityReading')}
                id="product-retrievedYearlyQuantityReading"
                name="retrievedYearlyQuantityReading"
                data-cy="retrievedYearlyQuantityReading"
                type="text"
              />
              <ValidatedField
                label={translate('crawlerBackendApp.product.initialFixedAnnualPrice')}
                id="product-initialFixedAnnualPrice"
                name="initialFixedAnnualPrice"
                data-cy="initialFixedAnnualPrice"
                type="text"
              />
              <ValidatedField
                label={translate('crawlerBackendApp.product.activeDate')}
                id="product-activeDate"
                name="activeDate"
                data-cy="activeDate"
                type="date"
              />
              <ValidatedField
                label={translate('crawlerBackendApp.product.inactiveDate')}
                id="product-inactiveDate"
                name="inactiveDate"
                data-cy="inactiveDate"
                type="date"
              />
              <ValidatedField
                label={translate('crawlerBackendApp.product.usage')}
                id="product-usage"
                name="usage"
                data-cy="usage"
                type="text"
              />
              <ValidatedField
                label={translate('crawlerBackendApp.product.desireStartDate')}
                id="product-desireStartDate"
                name="desireStartDate"
                data-cy="desireStartDate"
                type="date"
              />
              <ValidatedField
                label={translate('crawlerBackendApp.product.desireEndDate')}
                id="product-desireEndDate"
                name="desireEndDate"
                data-cy="desireEndDate"
                type="date"
              />
              <ValidatedField
                id="product-productOrder"
                name="productOrder"
                data-cy="productOrder"
                label={translate('crawlerBackendApp.product.productOrder')}
                type="select"
              >
                <option value="" key="0" />
                {productOrders
                  ? productOrders.map(otherEntity => (
                      <option value={otherEntity.id} key={otherEntity.id}>
                        {otherEntity.id}
                      </option>
                    ))
                  : null}
              </ValidatedField>
              <ValidatedField
                id="product-productOffering"
                name="productOffering"
                data-cy="productOffering"
                label={translate('crawlerBackendApp.product.productOffering')}
                type="select"
              >
                <option value="" key="0" />
                {productOfferings
                  ? productOfferings.map(otherEntity => (
                      <option value={otherEntity.id} key={otherEntity.id}>
                        {otherEntity.id}
                      </option>
                    ))
                  : null}
              </ValidatedField>
              <ValidatedField
                id="product-ean"
                name="ean"
                data-cy="ean"
                label={translate('crawlerBackendApp.product.ean')}
                type="select"
              >
                <option value="" key="0" />
                {eans
                  ? eans.map(otherEntity => (
                      <option value={otherEntity.id} key={otherEntity.id}>
                        {otherEntity.id}
                      </option>
                    ))
                  : null}
              </ValidatedField>
              <Button tag={Link} id="cancel-save" data-cy="entityCreateCancelButton" to="/product" replace color="info">
                <FontAwesomeIcon icon="arrow-left" />
                &nbsp;
                <span className="d-none d-md-inline">
                  <Translate contentKey="entity.action.back">Back</Translate>
                </span>
              </Button>
              &nbsp;
              <Button
                color="primary"
                id="save-entity"
                data-cy="entityCreateSaveButton"
                type="submit"
                disabled={updating}
              >
                <FontAwesomeIcon icon="save" />
                &nbsp;
                <Translate contentKey="entity.action.save">Save</Translate>
              </Button>
            </ValidatedForm>
          )}
        </Col>
      </Row>
    </div>
  );
};

export default ProductUpdate;
